import type { LiteralUnion } from "type-fest";

export const partners = [
    "aafes",
    "adt",
    "amazon",
    "amazonrouter",
    "armstrongmccall",
    "asurion",
    "asurionhomeplus",
    "asuriontechcare",
    "att",
    "attconnectedhome",
    "backmarket",
    "bjs",
    "blains",
    "bluestream",
    "brightspeed",
    "cactus",
    "cgx",
    "clyde",
    "comcastbusiness",
    "cosmoprof",
    "cox",
    "cricket",
    "dell",
    "dish",
    "dsg",
    "ee",
    "expertuniversity",
    "fieldstream",
    "fingerhut",
    "fios",
    "frontier",
    "gettington",
    "golfgalaxy",
    "guitarcenter",
    "homepro",
    "hotwire",
    "hotwirecommunications",
    "hpsales",
    "jcp",
    "koodo",
    "leakprotect",
    "lgelectronics",
    "liberty",
    "life360",
    "mcafee",
    "mcafeesecurityscan",
    "mcafeetechsupport",
    "mcx",
    "mediacom",
    "meijer",
    "microsoft",
    "nex",
    "nexcom",
    "nrg",
    "optimum",
    "optimumbiz",
    "others",
    "overstock",
    "paycheckdirect",
    "pilot",
    "quill",
    "reach",
    "sallybeauty",
    "samsung",
    "samsungna",
    "shopko",
    "smartpay",
    "soluto",
    "sparklight",
    "sparklightsales",
    "sprint",
    "staples",
    "stapleshome",
    "staplessmb",
    "telus",
    "telushomepro",
    "thesource",
    "tmobile",
    "tracfone",
    "tsc",
    "unknown",
    "uscellular",
    "vcs",
    "verizon",
    "verizonconnectedhome",
    "verizonwholeoffice",
    "viasat",
    "vip",
    "vzwsmb",
    "xboxcareplus",
    "yahoo",
    "youtubetv",
] as const;
/** known partner or any string */
export type Partner = LiteralUnion<(typeof partners)[number], string>;

/**
 * Converts a partner value from UI display text to the canonical partner **key** expected
 * by APIs and backends: lowercase, with spaces and `&` removed (e.g. how "AT&T" is shown
 * to users vs. the `att` slug sent in requests).
 *
 * @param partner - Label or mixed-case string from the UI (e.g. `"AT&T"`)
 *   or any string to normalize toward the backend form.
 * @returns The normalized key, e.g. `"AT&T"` → `"att"`, `"Verizon"` → `"verizon"`.
 *
 * @example
 * normalizePartnerKeyForApi("AT&T"); // "att"
 */
export function normalizePartnerKeyForApi(partner: string): string {
    return partner.toLowerCase().replace(/&/g, "").replace(/ /g, "");
}

/** Partner keys that belong to the Staples line of business. */
export const staplesPartners = ["staples", "stapleshome", "staplessmb"] as const satisfies readonly Partner[];

/**
 * Returns `true` when the given partner belongs to the Staples family
 * (`staples`, `stapleshome`, or `staplessmb`).
 *
 * Matching is tolerant of UI labels: the value is normalized with
 * {@link normalizePartnerKeyForApi} first, so both the canonical key
 * (`"stapleshome"`) and the display label (`"Staples Home"`) resolve to `true`.
 *
 * @param partner - Partner key or label, or `undefined` when no partner is set
 *   (e.g. the expert is not on a call).
 * @returns Whether the partner is a Staples partner.
 *
 * @example
 * isStaplesPartner("staples"); // true
 * isStaplesPartner("Staples Home"); // true
 * isStaplesPartner("verizon"); // false
 * isStaplesPartner(undefined); // false
 */
export function isStaplesPartner(partner: string | undefined): boolean {
    if (partner === undefined) {
        return false;
    }
    return (staplesPartners as readonly string[]).includes(normalizePartnerKeyForApi(partner));
}
